import type {ChangeEvent, ReactElement} from 'react';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {getVideoId, getVideoThumbnailURL, getVideoType} from '@Libraries/embedded-video-library';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {openErrorModal} from '@Modals/error-modal';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {
  addComponentAnchor,
  DATA_URL_ATTRIBUTE,
  getValidatedUrl,
  isValidForLink,
  isVideoLinkWrapper,
  removeComponentAnchor,
} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {setImageLoading, setEmbeddedVideoSrc} from '@EmailMakerReact/lib/email-maker-media-library';
import {getSignupFormsData} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import {useComponentAttribute} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControlProps} from '../../right-panel-registry.types';
import {LINK_INPUT_DEBOUNCE_MS, LINK_INPUT_MAX_LENGTH} from './link-wrapper-control.types';
import {SignupFormSuggestions} from './components/signup-form-suggestions/signup-form-suggestions';
import styles from './link-wrapper-control.module.scss';

export function LinkWrapperControl({component}: EmailMakerControlProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  const liveUrl = useComponentAttribute(component, DATA_URL_ATTRIBUTE);

  const [inputValue, setInputValue] = useState<string>(liveUrl);
  const [showError, setShowError] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasSuggestions = getSignupFormsData().signupForms.length > 0;

  const commitUrl = useCallback(
    (raw: string): void => {
      if (!editor) {
        return;
      }

      const normalized = getValidatedUrl(raw);
      setInputValue(normalized);

      if (isVideoLinkWrapper(component)) {
        const videoId = getVideoId(normalized);
        const videoType = getVideoType(normalized);

        if (!videoType || !videoId) {
          openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
          setImageLoading(editor, {component, loading: false});
          return;
        }

        setImageLoading(editor, {component, loading: true, text: window.i18next.t('pmwjs_loading')});

        void getVideoThumbnailURL(videoId, videoType)
          .then((thumbnailURL: string) => {
            setEmbeddedVideoSrc(editor, {component, thumbnailURL, videoURL: normalized});
          })
          .catch((e: unknown) => {
            const message = typeof e === 'string' ? e : window.i18next.t('pmwjs_error_try_again_reload_page');
            openErrorModal({message});
            setImageLoading(editor, {component, loading: false});
          });

        return;
      }

      if (normalized.length > 0) {
        addComponentAnchor(editor, {component, url: normalized});
      } else {
        removeComponentAnchor(editor, component);
      }

      if (normalized === '' || isValidForLink(normalized)) {
        setShowError(false);
      } else {
        setShowError(true);
      }
    },
    [editor, component]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;
      setInputValue(raw);
      setShowSuggestions(false);

      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout((): void => {
        commitUrl(raw);
      }, LINK_INPUT_DEBOUNCE_MS);
    },
    [commitUrl]
  );

  const handleFocus = useCallback((): void => {
    setIsFocused(true);
    if (hasSuggestions) {
      setShowSuggestions(true);
    }
  }, [hasSuggestions]);

  const handleBlur = useCallback((): void => {
    setIsFocused(false);
  }, []);

  const handleSuggestionSelect = useCallback(
    (url: string): void => {
      setInputValue(url);
      setShowSuggestions(false);
      setShowError(false);
      commitUrl(url);
    },
    [commitUrl]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return (): void => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return (): void => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setInputValue(liveUrl);
  }, [liveUrl]);

  useEffect(() => {
    if (liveUrl && inputRef.current) {
      inputRef.current.focus();
    }
  }, [liveUrl]);

  function renderError(): ReactElement | null {
    if (!showError) {
      return null;
    }
    return (
      <div className={styles.errorContainer}>
        <Icon ariaHidden className="_unmargin" icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_invalid_format')} size={TextSize.SM} className={styles.errorText} />
      </div>
    );
  }

  function renderSuggestions(): ReactElement | null {
    if (!hasSuggestions) {
      return null;
    }
    return <SignupFormSuggestions currentUrl={inputValue} isVisible={showSuggestions} onSelectUrl={handleSuggestionSelect} />;
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Text val={window.i18next.t('pmwjs_link')} size={TextSize.SM} bold className={styles.label} />
      <div className={`${styles.inputContainer} ${showError ? '-error' : ''} ${isFocused ? '-selected' : ''}`}>
        <Icon ariaHidden className={styles.inputIcon} icon="icon-link" size={IconSize.SIZE_ICON_16} />
        <input
          aria-label={window.i18next.t('pmwjs_link_placeholder')}
          className={styles.input}
          maxLength={LINK_INPUT_MAX_LENGTH}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={window.i18next.t('pmwjs_link_placeholder')}
          ref={inputRef}
          type="url"
          value={inputValue}
        />
      </div>
      {renderError()}
      {renderSuggestions()}
    </div>
  );
}
